/**
 * Where a new plan fits on a day: the day's busy spans (timed stops, parts of
 * the day, trains and flights), the free gaps between them, and the ONE start
 * rule every add surface uses (Kurtis 2026-10-23) — the stop before ends, plus
 * the time to get here from it, rounded up to 5 minutes. The add sheet's
 * "Start 2:20 PM" gap rows, its suggestion, and the map pin card all read it,
 * so a start never looks like a random buffer.
 *
 * Minutes since midnight inside; "HH:MM" strings at the edges.
 * Pure — no React, no i18n, no clock.
 */
import { getActivityCoords } from './travelEstimates';
import { distanceMeters, estimateWalkingMinutes } from './tourWalkability';
import { transportDayRole } from './transportUtils';
import { isQuickStop, stopLengthMinutes } from './quickStops';
import { dayWalks } from './dayWalks';

// The part-of-day windows an untimed stop holds for slot purposes. Their
// starts match TripDayTimePicker's TIME_BLOCKS defaults (09:00 / 13:00 /
// 18:00); the ends are getTimeBlock's boundaries (noon, 17:00) and a
// 22:00 evening close.
export const BLOCK_WINDOWS = {
  morning: [9 * 60, 12 * 60],
  afternoon: [13 * 60, 17 * 60],
  evening: [18 * 60, 22 * 60],
};

const DEFAULT_DURATION = 60;
// Past a 45-minute walk nobody walks: a rough ride instead (10 min to get
// going + 2 min per km), so a stop across town isn't scheduled 2 h later.
const MAX_WALK_MIN = 45;

export const toMinutes = (time) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(time || ''));
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
};

export const toTimeString = (min) => {
  const v = Math.max(0, Math.min(24 * 60 - 1, Math.round(min)));
  return `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
};

const localMinutes = (local) => toMinutes(String(local || '').slice(11, 16));

const coordsFrom = (c) => {
  if (!c) return null;
  const lat = Number(c.lat ?? c.latitude);
  const lng = Number(c.lng ?? c.longitude);
  return c.lat == null && c.latitude == null ? null : (Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null);
};

const blockOf = (a) => a?.timeBlock || a?.time_block || null;
const titleOf = (a) => a?.title || a?.name || a?.customTitle || a?.custom_title || '';

/**
 * One day's busy spans, sorted by start: { start, end, kind, title, item,
 * coords, role?, quick? }. kind 'activity' (timed; `quick` for a quick stop —
 * utils/quickStops — whose end is its start), 'block' (untimed, held as its
 * part-of-day window), 'transport' (role: arrival 00:00→arrival, departure
 * departure→arrival or →24:00, transit all day). All-day stops take no span.
 * `opts.exclude` (an activity object) leaves one stop out — a reschedule
 * mustn't clash with itself — and the walk it is inside, if any.
 */
export function daySpans(day, transports = [], opts = {}) {
  const spans = [];
  // A place inside a walk is moved within the walk's own block: the walk
  // (utils/dayWalks) is no clash for it.
  const ownWalk = opts.exclude ? dayWalks(day?.activities).walkOf(opts.exclude) : null;
  (day?.activities || []).forEach((a) => {
    if (!a || a === opts.exclude || a === ownWalk) return;
    const block = blockOf(a);
    if (block === 'all_day') return;
    const start = toMinutes(a.scheduledTime);
    if (start !== null) {
      const quick = isQuickStop(a);
      const dur = stopLengthMinutes(a, DEFAULT_DURATION);
      spans.push({ start, end: Math.min(24 * 60, start + dur), kind: 'activity', title: titleOf(a), item: a, coords: getActivityCoords(a), ...(quick ? { quick: true } : {}) });
    } else if (BLOCK_WINDOWS[block]) {
      const [s, e] = BLOCK_WINDOWS[block];
      spans.push({ start: s, end: e, kind: 'block', title: titleOf(a), item: a, coords: getActivityCoords(a) });
    }
  });
  const date = day?.date;
  (transports || []).forEach((t) => {
    const role = date ? transportDayRole(t, date) : null;
    if (!role) return;
    const dep = localMinutes(t.departureLocal);
    const arr = localMinutes(t.arrivalLocal);
    const sameDay = String(t.arrivalLocal || '').slice(0, 10) === date;
    let start;
    let end;
    if (role === 'arrival') { start = 0; end = arr ?? 0; }
    else if (role === 'transit') { start = 0; end = 24 * 60; }
    else { start = dep ?? 0; end = sameDay && arr !== null && arr > start ? arr : 24 * 60; }
    // A transport's "where you are after it" is its destination.
    spans.push({ start, end, kind: 'transport', role, title: t.destinationName || t.destinationCity || '', item: t, coords: coordsFrom(t.destinationCoordinates) });
  });
  return spans.sort((a, b) => a.start - b.start || a.end - b.end);
}

/** Free gaps of at least `minGap` minutes inside [dayStart, dayEnd]: { start, end, prev } (prev: the span ending at the gap's start, or null). */
export function freeGaps(spans, { dayStart = 8 * 60, dayEnd = 23 * 60, minGap = 30 } = {}) {
  const sorted = (spans || []).slice().sort((a, b) => a.start - b.start);
  const gaps = [];
  let cur = dayStart;
  let prev = null;
  sorted.forEach((s) => {
    if (s.start - cur >= minGap) gaps.push({ start: cur, end: s.start, prev });
    if (s.end >= cur) { cur = s.end; prev = s; }
  });
  if (dayEnd - cur >= minGap) gaps.push({ start: cur, end: dayEnd, prev });
  return gaps;
}

/** Minutes to get from a span's place to `toCoords`: a straight-line walk (the walking-tour editor's estimate), a rough ride past 45 min, 0 when either side has no position. */
export function travelMinutesBetween(fromSpan, toCoords) {
  const from = fromSpan?.coords;
  if (!from || !toCoords) return 0;
  const meters = distanceMeters(from, toCoords);
  if (!meters) return 0;
  const walk = estimateWalkingMinutes(meters);
  return walk <= MAX_WALK_MIN ? walk : Math.round(10 + (meters / 1000) * 2);
}

const up5 = (m) => Math.ceil(m / 5) * 5;

/** The start right after `prevSpan`: its end plus the trip from it, to the next 5 minutes. No span → null. */
export function startAfter(prevSpan, newCoords) {
  if (!prevSpan) return null;
  return up5(prevSpan.end + travelMinutesBetween(prevSpan, newCoords));
}

/** Where a gap's new plan starts: right after the stop before it (startAfter), or the gap's own start when nothing precedes it. */
export function gapStart(gap, newCoords) {
  return gap.prev ? Math.max(gap.start, startAfter(gap.prev, newCoords)) : up5(gap.start);
}

/**
 * The suggested start for a plan of `durationMin`: the first gap it fits in
 * (counting the trip from the stop before), else the first gap. → { time,
 * minutes, afterTitle, afterKind, travelMinutes } or null when the day has no
 * gap at all.
 */
export function suggestStart(spans, durationMin, newCoords, gapOpts) {
  const gaps = freeGaps(spans, gapOpts);
  if (!gaps.length) return null;
  const dur = durationMin > 0 ? durationMin : DEFAULT_DURATION;
  const fit = gaps.find(g => g.end - gapStart(g, newCoords) >= dur) || gaps[0];
  const minutes = gapStart(fit, newCoords);
  return {
    time: toTimeString(minutes),
    minutes,
    afterTitle: fit.prev ? fit.prev.title || null : null,
    afterKind: fit.prev ? fit.prev.kind : null,
    travelMinutes: fit.prev ? travelMinutesBetween(fit.prev, newCoords) : 0,
  };
}

/**
 * The span a plan at [startMin, startMin + durationMin) overlaps — a transport
 * first — or null. Part-of-day windows never clash (they're loose), and
 * neither does a quick stop, either way round (`opts.quick`: the plan is one).
 */
export function clashWith(spans, startMin, durationMin, opts = {}) {
  if (startMin == null || opts.quick) return null;
  const end = startMin + (durationMin > 0 ? durationMin : DEFAULT_DURATION);
  const hits = (spans || []).filter(s => s.kind !== 'block' && !s.quick && s.start < end && s.end > startMin);
  return hits.find(s => s.kind === 'transport') || hits[0] || null;
}
